import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { ModuleAuditQuery } from "@/types/module-kill-switch";
import { useKillSwitchAudit } from "../hooks/useKillSwitches";
import { AuditFilters, AuditFilterValues } from "./AuditFilters";
import { AuditTable } from "./AuditTable";

const DEFAULT_LIMIT = 20;

export const AuditLogSection: React.FC = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditFilterValues>({
    module: "",
    userId: "",
    dateFrom: null,
    dateTo: null,
  });
  const [page, setPage] = useState(1);
  const debouncedUserId = useDebounce(filters.userId, 400);

  const query = useMemo<ModuleAuditQuery>(() => {
    const trimmedUser = debouncedUserId.trim();
    return {
      module: filters.module || undefined,
      userId: trimmedUser ? trimmedUser : undefined,
      dateFrom: filters.dateFrom ? filters.dateFrom.toISOString() : undefined,
      dateTo: filters.dateTo ? endOfDayIso(filters.dateTo) : undefined,
      page,
      limit: DEFAULT_LIMIT,
    };
  }, [filters.module, filters.dateFrom, filters.dateTo, debouncedUserId, page]);

  const { data, isLoading, isError } = useKillSwitchAudit(query);

  const handleFiltersChange = (next: AuditFilterValues) => {
    setFilters(next);
    setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-foreground">
          {t("modules.audit.sectionTitle")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("modules.audit.sectionDescription")}
        </p>
      </header>

      <AuditFilters value={filters} onChange={handleFiltersChange} />

      <AuditTable
        entries={data?.data ?? []}
        isLoading={isLoading}
        isError={isError}
      />

      {data && data.total > data.limit && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                className={cn(
                  "cursor-pointer",
                  page === 1 && "pointer-events-none opacity-50",
                )}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              />
            </PaginationItem>
            {pageNumbers.map((p) => (
              <PaginationItem key={p}>
                <PaginationLink
                  isActive={page === p}
                  onClick={() => setPage(p)}
                  className="cursor-pointer"
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                className={cn(
                  "cursor-pointer",
                  page >= totalPages && "pointer-events-none opacity-50",
                )}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </section>
  );
};

const endOfDayIso = (date: Date): string => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end.toISOString();
};

const buildPageNumbers = (
  current: number,
  total: number,
  windowSize = 5,
): number[] => {
  if (total <= windowSize) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, current - half);
  let end = start + windowSize - 1;
  if (end > total) {
    end = total;
    start = end - windowSize + 1;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};
